import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { PageHeader, Loading, BackButton } from '../components/Shared';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ModalNouvelleSession } from './Sessions';


const SOURCE_COLOR = { manuel: '#ef4444', devis: '#f97316', convention: '#16a34a' };
const SOURCE_LABEL = { manuel: 'Ajout manuel (projet)', devis: 'Fiche validée — convention en attente', convention: 'Convention signée (confirmé)' };

function SessionTooltip({ ev, pos, sessionDetail, loading, onMouseEnter, onMouseLeave }) {
  if (!pos) return null;

  const style = {
    position: 'fixed',
    zIndex: 9999,
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
    padding: '0.85rem 1rem',
    minWidth: '260px',
    maxWidth: '340px',
    fontSize: '0.82rem',
    color: 'var(--text-primary)',
    cursor: 'default',
  };

  // Positionnement : à droite si possible, sinon à gauche
  const left = pos.right + 12 < window.innerWidth - 360 ? pos.right + 12 : pos.left - 360;
  const maxH = window.innerHeight - 20;          // 10px marge haut + bas
  const top  = Math.min(Math.max(10, pos.top), window.innerHeight - Math.min(maxH, 600));

  // Groupes + inscriptions depuis le détail session
  const groupes     = sessionDetail?.groupes     || [];
  const inscriptions = sessionDetail?.inscriptions || [];

  // Grouper les inscriptions par sous_session_id
  const insByGroupe = {};
  inscriptions.forEach(i => {
    const key = i.sous_session_id || '__none';
    if (!insByGroupe[key]) insByGroupe[key] = [];
    insByGroupe[key].push(i);
  });

  return (
    <div style={{ ...style, left, top, maxHeight: maxH + 'px', overflowY: 'auto' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {/* En-tête */}
      <div style={{ marginBottom: '0.55rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
        <div style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--accent-blue)', marginBottom: '0.15rem' }}>{ev.session_id}</div>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.3 }}>{ev.intitule_formation}</div>
      </div>

      {/* Infos session */}
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.2rem 0.6rem', marginBottom: '0.6rem', fontSize: '0.78rem' }}>
        {ev.nom_formateur && <>
          <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Formateur</span>
          <span>{ev.nom_formateur}</span>
        </>}
        {ev.duree && <>
          <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Durée</span>
          <span>{parseFloat(ev.duree)} h</span>
        </>}
        {ev.nom_salle && <>
          <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Salle</span>
          <span>{ev.nom_salle}</span>
        </>}
        {!ev.nom_salle && ev.lieu && <>
          <span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Lieu</span>
          <span style={{ fontSize: '0.75rem' }}>{ev.lieu}</span>
        </>}
      </div>

      {/* Groupes + stagiaires */}
      {loading ? (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Chargement des groupes…</div>
      ) : groupes.length === 0 ? (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Aucun groupe enregistré</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {groupes.map(g => {
            const stags = insByGroupe[g.sous_session_id] || [];
            return (
              <div key={g.sous_session_id} style={{ background: 'var(--bg-elevated)', borderRadius: '7px', padding: '0.35rem 0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: stags.length ? '0.25rem' : 0 }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', background: '#e2e8f0', padding: '0.1rem 0.45rem', borderRadius: '4px' }}>
                    Gr. {g.groupe}
                  </span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{g.nom_client || '—'}</span>
                </div>
                {stags.map((st, idx) => {
                  const src = st.source || 'convention';
                  const col = SOURCE_COLOR[src] || '#94a3b8';
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: '0.3rem', marginTop: '0.15rem' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: col, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{st.nom_stagiaire_snapshot || st.stagiaire_id}</span>
                    </div>
                  );
                })}
                {!stags.length && (
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginLeft: '0.3rem', fontStyle: 'italic' }}>Aucun stagiaire</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Légende source */}
      <div style={{ marginTop: '0.55rem', paddingTop: '0.4rem', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.18rem' }}>
        {Object.entries(SOURCE_COLOR).map(([src, col]) => (
          <div key={src} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: col, flexShrink: 0 }} />
            <span style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>{SOURCE_LABEL[src]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const FALLBACK_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
const MOIS_NOMS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const JOURS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];
const JOURS_5 = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi'];

function FilterDropdown({ label, options, value, onChange, colorMap }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = value !== null;
  const selectedLabel = options.find(o => o.value === value)?.label;

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.35rem',
          padding: '0.4rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
          fontSize: '0.82rem', fontWeight: active ? 700 : 500,
          border: `1.5px solid ${active ? 'var(--accent-blue)' : 'var(--border)'}`,
          background: active ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)',
          color: active ? 'var(--accent-blue)' : 'var(--text-secondary)',
          whiteSpace: 'nowrap', transition: 'all 0.15s',
        }}>
        <span>{active ? selectedLabel : label}</span>
        {active
          ? <span onClick={e => { e.stopPropagation(); onChange(null); }} style={{ opacity: 0.7, fontWeight: 700, fontSize: '0.85rem', lineHeight: 1 }}>×</span>
          : <span style={{ fontSize: '0.6rem', opacity: 0.5 }}>▼</span>
        }
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          minWidth: '200px', maxWidth: '300px', maxHeight: '280px',
          overflowY: 'auto', padding: '0.35rem',
        }}>
          {options.length === 0
            ? <div style={{ padding: '0.5rem 0.65rem', fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>Aucune option</div>
            : options.map(opt => {
              const isSelected = value === opt.value;
              const color = colorMap?.[opt.value];
              return (
                <div key={opt.value}
                  onClick={() => { onChange(isSelected ? null : opt.value); setOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.45rem 0.65rem', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '0.82rem', fontWeight: isSelected ? 700 : 400,
                    background: isSelected ? 'rgba(59,130,246,0.1)' : 'transparent',
                    color: isSelected ? 'var(--accent-blue)' : 'var(--text-primary)',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                  {color && <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: color, flexShrink: 0, display: 'inline-block' }} />}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</span>
                  {isSelected && <span style={{ marginLeft: 'auto' }}>✓</span>}
                </div>
              );
            })
          }
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPRESSION
// ─────────────────────────────────────────────────────────────────────────────

function escHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

const PRINT_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];

/** Construit le tableau de cellules Lun–Ven pour un mois donné */
function buildPrintCells(annee, mois) {
  const fd = new Date(annee, mois - 1, 1);
  const ld = new Date(annee, mois, 0);
  const dow0 = fd.getDay();
  const toMon = dow0 === 0 ? 6 : dow0 - 1;
  const start = new Date(fd);
  start.setDate(start.getDate() - toMon);
  const result = [];
  const cur = new Date(start);
  while (true) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) {
      result.push(cur.getMonth() === mois - 1 && cur.getFullYear() === annee ? cur.getDate() : null);
    }
    cur.setDate(cur.getDate() + 1);
    if (cur > ld && cur.getDay() === 1) break;
    if (result.length > 250) break;
  }
  while (result.length % 5 !== 0) result.push(null);
  return result;
}

/** Génère le HTML complet pour l'impression multi-mois */
function genererHTMLImpress(moisData, MOIS_NOMS) {
  const today = new Date().toLocaleDateString('fr-FR');

  const pagesHTML = moisData.map(({ annee, mois, events }, pageIdx) => {
    // Palette couleurs par salle/session
    const colorMap = {};
    let ci = 0;
    events.forEach(ev => {
      const key = ev.salle_id || ev.session_id;
      if (!colorMap[key]) colorMap[key] = ev.salle_couleur || PRINT_COLORS[ci++ % PRINT_COLORS.length];
    });

    // Regrouper par jour (week-ends exclus)
    const byDay = {};
    events.forEach(ev => {
      const dateObj = new Date(ev.date);
      const dow = dateObj.getDay();
      if (dow === 0 || dow === 6) return;
      const d = dateObj.getDate();
      if (!byDay[d]) byDay[d] = [];
      byDay[d].push(ev);
    });

    const cells = buildPrintCells(annee, mois);

    const cellsHTML = cells.map(day => {
      if (!day) return `<td class="cal-cell cal-empty"></td>`;
      const evs = byDay[day] || [];
      const evsHTML = evs.map(ev => {
        const color = ev.salle_id ? colorMap[ev.salle_id] : (colorMap[ev.session_id] || PRINT_COLORS[0]);
        const inscrits = (ev.nb_inscrits > 0 || ev.stagiaires_max > 0)
          ? `${ev.nb_inscrits ?? 0}${ev.stagiaires_max ? `/${ev.stagiaires_max}` : ''}` : '';
        const infoLabel = inscrits;
        const titre = escHtml((ev.nom_dossier || ev.intitule_formation || '').substring(0, 38));
        const clients = escHtml(ev.noms_clients || '');
        const meta = [ev.nom_salle, ev.nom_formateur].filter(Boolean).map(escHtml).join(' · ');
        const modules = ev.modules_du_jour ? escHtml('↳ ' + ev.modules_du_jour) : '';

        return `<div class="ev" style="background:${color}">
  <div class="ev-hd">
    <b>${escHtml(ev.session_id)}</b>
    ${infoLabel ? `<span class="ev-slot">${escHtml(infoLabel)}</span>` : ''}
  </div>
  ${titre ? `<div class="ev-title">${titre}</div>` : ''}
  ${modules ? `<div class="ev-modules" style="font-size:7.5pt;font-style:italic;opacity:0.92;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${modules}</div>` : ''}
  ${clients ? `<div class="ev-clients">${clients}</div>` : ''}
  ${meta ? `<div class="ev-meta">${meta}</div>` : ''}
</div>`;
      }).join('');

      return `<td class="cal-cell"><div class="day-num">${day}</div>${evsHTML}</td>`;
    });

    const rows = [];
    for (let i = 0; i < cells.length; i += 5) {
      rows.push(`<tr>${cellsHTML.slice(i, i + 5).join('')}</tr>`);
    }

    const isLast = pageIdx === moisData.length - 1;
    return `<div class="month-page${isLast ? ' last' : ''}">
  <div class="month-hd">
    <span class="month-title">Planning des formations — ${MOIS_NOMS[mois - 1]} ${annee}</span>
    <span class="month-printed">Imprimé le ${today}</span>
  </div>
  <table><thead><tr>
    <th>Lundi</th><th>Mardi</th><th>Mercredi</th><th>Jeudi</th><th>Vendredi</th>
  </tr></thead><tbody>${rows.join('')}</tbody></table>
</div>`;
  });

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>Planning des formations</title>
<style>
@page{size:A4 landscape;margin:.7cm}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:8pt;color:#1e293b;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.month-page{page-break-after:always}
.month-page.last{page-break-after:avoid}
.month-hd{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:5pt;border-bottom:2px solid #1e40af;padding-bottom:2pt}
.month-title{font-size:12pt;font-weight:bold;color:#1e40af}
.month-printed{font-size:7pt;color:#94a3b8}
table{width:100%;border-collapse:collapse;table-layout:fixed}
th{background:#1e40af;color:#fff;font-size:8pt;text-align:center;padding:3pt 2pt;border:1px solid #1e3a8a}
.cal-cell{border:1px solid #cbd5e1;vertical-align:top;padding:2pt;min-height:65pt;width:20%}
.cal-empty{background:#f8fafc}
.day-num{font-size:7.5pt;font-weight:bold;color:#64748b;text-align:right;margin-bottom:2pt;font-variant-numeric:normal;font-feature-settings:"zero" 0}
.ev{border-radius:2pt;padding:2pt 3pt;margin-bottom:2pt;color:#fff;font-size:7pt;line-height:1.3;overflow:hidden}
.ev-hd{display:flex;align-items:center;gap:3pt;margin-bottom:1pt;flex-wrap:nowrap;overflow:hidden}
.ev-hd b{font-size:7pt;white-space:nowrap}
.ev-slot{font-size:6.5pt;opacity:.88;white-space:nowrap}
.ev-nb{font-size:6.5pt;opacity:.85;margin-left:auto;white-space:nowrap;font-variant-numeric:normal;font-feature-settings:"zero" 0}
.ev-title{font-size:7pt;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:1pt}
.ev-clients{font-size:6.5pt;font-style:italic;opacity:.92;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:1pt}
.ev-meta{font-size:6pt;opacity:.8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
</style></head><body>
${pagesHTML.join('\n')}
<script>window.onafterprint=function(){window.close();};</script>
</body></html>`;
}

/** Modale de sélection des mois à imprimer */
function PrintModal({ annee, mois, onClose }) {
  const MOIS = MOIS_NOMS;
  // 12 mois à partir du mois courant
  const options = [];
  for (let i = 0; i < 12; i++) {
    let m = mois + i, a = annee;
    while (m > 12) { m -= 12; a++; }
    options.push({ annee: a, mois: m });
  }

  const [selected, setSelected] = useState([{ annee, mois }]);
  const [printing, setPrinting] = useState(false);

  function toggle(opt) {
    setSelected(prev => {
      const exists = prev.some(s => s.annee === opt.annee && s.mois === opt.mois);
      const next = exists
        ? prev.filter(s => !(s.annee === opt.annee && s.mois === opt.mois))
        : [...prev, opt];
      return next.sort((a, b) => a.annee !== b.annee ? a.annee - b.annee : a.mois - b.mois);
    });
  }

  async function handlePrint() {
    if (!selected.length) return;
    setPrinting(true);
    try {
      const moisData = await Promise.all(
        selected.map(async ({ annee, mois }) => {
          const d = await apiFetch(`/calendrier?annee=${annee}&mois=${mois}`);
          return { annee, mois, events: d.data || [] };
        })
      );
      const html = genererHTMLImpress(moisData, MOIS);
      const win = window.open('', '_blank', 'width=1200,height=900');
      if (!win) { alert('Veuillez autoriser les pop-ups pour imprimer.'); setPrinting(false); return; }
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 600);
    } catch (e) {
      alert('Erreur lors de la génération : ' + e.message);
    }
    setPrinting(false);
  }

  const nb = selected.length;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'1.75rem', width:'420px', maxWidth:'95vw' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
          <h3 style={{ margin:0, fontSize:'1rem', color:'var(--text-primary)' }}>🖨 Imprimer le planning</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-tertiary)', fontSize:'1.2rem', cursor:'pointer' }}>✕</button>
        </div>

        <p style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:'0.75rem' }}>
          Format A4 paysage · 1 page par mois · Semaine Lun–Ven
        </p>

        <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', marginBottom:'1rem', maxHeight:'290px', overflowY:'auto' }}>
          {options.map(opt => {
            const sel = selected.some(s => s.annee === opt.annee && s.mois === opt.mois);
            return (
              <label key={`${opt.annee}-${opt.mois}`} style={{
                display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer',
                padding:'0.42rem 0.65rem', borderRadius:'7px',
                background: sel ? 'rgba(59,130,246,0.1)' : 'transparent',
                border: `1px solid ${sel ? 'var(--accent-blue)' : 'var(--border)'}`,
                transition:'all 0.12s',
              }}>
                <input type="checkbox" checked={sel} onChange={() => toggle(opt)} style={{ accentColor:'var(--accent-blue)', flexShrink:0 }} />
                <span style={{ fontSize:'0.88rem', fontWeight: sel ? 600 : 400, color: sel ? 'var(--accent-blue)' : 'var(--text-primary)' }}>
                  {MOIS[opt.mois - 1]} {opt.annee}
                </span>
              </label>
            );
          })}
        </div>

        <p style={{ fontSize:'0.78rem', color:'var(--text-tertiary)', marginBottom:'1rem' }}>
          {nb > 0 ? `${nb} mois sélectionné${nb > 1 ? 's' : ''} — ${nb} page${nb > 1 ? 's' : ''}` : 'Aucun mois sélectionné'}
        </p>

        <div style={{ display:'flex', gap:'0.5rem', justifyContent:'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={printing}>Annuler</button>
          <button className="btn-primary" onClick={handlePrint} disabled={printing || nb === 0}>
            {printing ? 'Génération…' : `🖨 Imprimer${nb > 0 ? ` (${nb} page${nb > 1 ? 's' : ''})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

export default function Calendrier() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const now = new Date();
  const [annee, setAnnee] = useState(now.getFullYear());
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [modal, setModal] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const [cinqJours, setCinqJours] = useState(true);

  const [filtSalle, setFiltSalle] = useState(null);
  const [filtLieu, setFiltLieu] = useState(null);
  const [filtFormateur, setFiltFormateur] = useState(null);
  const [filtFormation, setFiltFormation] = useState(null);
  const [filtSlot, setFiltSlot] = useState(null);

  const { data, loading } = useApi('/calendrier', { annee, mois }, [annee, mois, refresh]);
  const { data: sallesData } = useApi('/salles');

  // Tooltip au survol
  const [tooltip, setTooltip] = useState(null); // { ev, pos }
  const [tooltipDetail, setTooltipDetail] = useState(null);
  const [tooltipLoading, setTooltipLoading] = useState(false);
  const hoverTimer = useRef(null);
  const sessionCache = useRef({});

  const handleEventEnter = useCallback((e, ev) => {
    clearTimeout(hoverTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    hoverTimer.current = setTimeout(async () => {
      setTooltip({ ev, pos: rect });
      setTooltipDetail(sessionCache.current[ev.session_id] || null);
      if (!sessionCache.current[ev.session_id]) {
        setTooltipLoading(true);
        try {
          const d = await apiFetch(`/sessions/${ev.session_id}`);
          sessionCache.current[ev.session_id] = d;
          setTooltipDetail(d);
        } catch {}
        setTooltipLoading(false);
      }
    }, 250);
  }, []);

  const hideTimer = useRef(null);

  const handleEventLeave = useCallback(() => {
    clearTimeout(hoverTimer.current);
    hideTimer.current = setTimeout(() => {
      setTooltip(null);
      setTooltipDetail(null);
    }, 120);
  }, []);

  const handleTooltipEnter = useCallback(() => {
    clearTimeout(hideTimer.current);
  }, []);

  const handleTooltipLeave = useCallback(() => {
    hideTimer.current = setTimeout(() => {
      setTooltip(null);
      setTooltipDetail(null);
    }, 80);
  }, []);

  const prevMonth = () => { if (mois === 1) { setMois(12); setAnnee(a => a - 1); } else setMois(m => m - 1); };
  const nextMonth = () => { if (mois === 12) { setMois(1); setAnnee(a => a + 1); } else setMois(m => m + 1); };

  const allEvents = data?.data || [];

  const AVANCEMENT_HEX = { red: '#dc2626', green: '#16a34a' };

  // Couleurs par salle (fallback palette uniquement si salle définie sans couleur)
  const salleColorMap = useMemo(() => {
    const map = {};
    let idx = 0;
    allEvents.forEach(ev => {
      if (!ev.salle_id) return;
      if (!map[ev.salle_id]) map[ev.salle_id] = ev.salle_couleur || FALLBACK_COLORS[idx++ % FALLBACK_COLORS.length];
    });
    return map;
  }, [allEvents]);

  // Sans salle : trait gris neutre
  const getColor = (ev) => ev.salle_id ? (salleColorMap[ev.salle_id] || FALLBACK_COLORS[0]) : '#64748b';

  // Options filtres
  const optsSalle = useMemo(() => {
    const seen = new Map();
    allEvents.forEach(ev => { if (ev.salle_id && !seen.has(ev.salle_id)) seen.set(ev.salle_id, ev.nom_salle || ev.salle_id); });
    return [...seen.entries()].map(([v, l]) => ({ value: v, label: l }));
  }, [allEvents]);

  const optsLieu = useMemo(() => {
    const seen = new Set();
    allEvents.forEach(ev => { if (ev.lieu) seen.add(ev.lieu); });
    return [...seen].sort().map(v => ({ value: v, label: v.length > 40 ? v.substring(0, 38) + '…' : v }));
  }, [allEvents]);

  const optsFormateur = useMemo(() => {
    const seen = new Set();
    allEvents.forEach(ev => { if (ev.nom_formateur) seen.add(ev.nom_formateur); });
    return [...seen].sort().map(v => ({ value: v, label: v }));
  }, [allEvents]);

  const optsFormation = useMemo(() => {
    const seen = new Set();
    allEvents.forEach(ev => { if (ev.intitule_formation) seen.add(ev.intitule_formation); });
    return [...seen].sort().map(v => ({ value: v, label: v.length > 50 ? v.substring(0, 48) + '…' : v }));
  }, [allEvents]);

  const optsSlot = [
    { value: 'journee', label: 'Journée' },
    { value: 'AM', label: 'Matin' },
    { value: 'PM', label: 'Après-midi' },
  ];

  const normalizeSlot = (slot) => {
    const s = (slot || '').toLowerCase().replace(/\s/g, '');
    if (s.includes('am') && s.includes('pm')) return 'journee';
    if (s === 'am') return 'AM';
    if (s === 'pm') return 'PM';
    return slot;
  };

  const filteredEvents = useMemo(() => allEvents.filter(ev => {
    if (filtSalle && ev.salle_id !== filtSalle) return false;
    if (filtLieu && ev.lieu !== filtLieu) return false;
    if (filtFormateur && ev.nom_formateur !== filtFormateur) return false;
    if (filtFormation && ev.intitule_formation !== filtFormation) return false;
    if (filtSlot && normalizeSlot(ev.slot) !== filtSlot) return false;
    // Masquer les dimanches toujours, les samedis sauf si session_samedi=true
    const dow = new Date(ev.date).getDay();
    if (dow === 0) return false; // dimanche : jamais
    if (dow === 6 && !ev.session_samedi) return false; // samedi : seulement si coché
    return true;
  }), [allEvents, filtSalle, filtLieu, filtFormateur, filtFormation, filtSlot]);

  const nbFiltresActifs = [filtSalle, filtLieu, filtFormateur, filtFormation, filtSlot].filter(Boolean).length;
  const resetFiltres = () => { setFiltSalle(null); setFiltLieu(null); setFiltFormateur(null); setFiltFormation(null); setFiltSlot(null); };

  // Grille calendrier
  const COLS = cinqJours ? 5 : 7;
  const JOURS_DISPLAY = cinqJours ? JOURS_5 : JOURS;

  // Rang stable par session : chaque session conserve la même ligne sur toute sa durée
  const sessionOrder = useMemo(() => {
    const firstDate = {};
    filteredEvents.forEach(ev => {
      if (!firstDate[ev.session_id] || ev.date < firstDate[ev.session_id]) {
        firstDate[ev.session_id] = ev.date;
      }
    });
    const sorted = Object.keys(firstDate).sort((a, b) => {
      const da = firstDate[a], db = firstDate[b];
      return da < db ? -1 : da > db ? 1 : a.localeCompare(b);
    });
    const order = {};
    sorted.forEach((sid, i) => { order[sid] = i; });
    return order;
  }, [filteredEvents]);

  // Regroupement par jour avec tri stable (même rang de session sur tous les jours)
  const eventsByDay = useMemo(() => {
    const map = {};
    filteredEvents.forEach(ev => {
      const d = new Date(ev.date).getDate();
      if (!map[d]) map[d] = [];
      map[d].push(ev);
    });
    Object.values(map).forEach(evs => {
      evs.sort((a, b) => {
        const oa = sessionOrder[a.session_id] ?? 9999;
        const ob = sessionOrder[b.session_id] ?? 9999;
        if (oa !== ob) return oa - ob;
        return (a.slot || '').localeCompare(b.slot || '');
      });
    });
    return map;
  }, [filteredEvents, sessionOrder]);

  // Construction de la grille
  const cells = useMemo(() => {
    if (!cinqJours) {
      // Mode 7 jours
      const startOffset = (new Date(annee, mois - 1, 1).getDay() + 6) % 7;
      const totalDays = new Date(annee, mois, 0).getDate();
      const result = [];
      for (let i = 0; i < startOffset; i++) result.push(null);
      for (let d = 1; d <= totalDays; d++) result.push(d);
      while (result.length % 7 !== 0) result.push(null);
      return result;
    } else {
      // Mode 5 jours : Mon–Ven uniquement
      const fd = new Date(annee, mois - 1, 1);
      const ld = new Date(annee, mois, 0);
      const dow0 = fd.getDay(); // 0=Sun…6=Sat
      const toMon = dow0 === 0 ? 6 : dow0 - 1;
      const start = new Date(fd);
      start.setDate(start.getDate() - toMon);
      const result = [];
      const cur = new Date(start);
      while (true) {
        const d = cur.getDay();
        if (d !== 0 && d !== 6) {
          const inMonth = cur.getMonth() === mois - 1 && cur.getFullYear() === annee;
          result.push(inMonth ? cur.getDate() : null);
        }
        cur.setDate(cur.getDate() + 1);
        // Stop when past end of month and we've finished the week (hit next Monday)
        if (cur > ld && cur.getDay() === 1) break;
        if (result.length > 250) break;
      }
      while (result.length % 5 !== 0) result.push(null);
      return result;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annee, mois, cinqJours]);

  // Hauteur de ligne dynamique + numéros de semaine + slots alignés
  const MAX_VISIBLE = 5;
  const SLOT_H = 46; // hauteur fixe d'un bloc événement (px) — identique pour les placeholders
  const ROW_HEIGHTS = [60, 60 + SLOT_H, 60 + SLOT_H * 2, 60 + SLOT_H * 3, 60 + SLOT_H * 4, 60 + SLOT_H * 5];
  const weekRowHeights = [];
  const weekNumbers = [];
  const slottedByDay = {}; // dayOfMonth → Array<ev | null>
  const extraByDay   = {}; // dayOfMonth → nb événements dépassant MAX_VISIBLE
  const numWeekRows  = cells.length / COLS;
  for (let row = 0; row < numWeekRows; row++) {
    // 1. Étendue de chaque session dans la semaine : de sa première à sa dernière colonne.
    const weekDays = [];
    const span = {}; // session_id → { from, to } (indices de colonnes)
    for (let col = 0; col < COLS; col++) {
      const day = cells[row * COLS + col];
      weekDays.push(day);
      if (day) (eventsByDay[day] || []).forEach(ev => {
        const sp = span[ev.session_id];
        if (!sp) span[ev.session_id] = { from: col, to: col };
        else sp.to = col;
      });
    }

    // 2. Une ligne (« couloir ») par session, la même sur toute sa durée : on prend la première ligne
    //    libre sur toute son étendue. Une ligne se libère dès qu'une session se termine, donc une session
    //    qui commence plus tard remonte dans la première ligne disponible (pas de case vide inutile).
    const sidsByStart = Object.keys(span).sort((a, b) =>
      (span[a].from - span[b].from) || ((sessionOrder[a] ?? 9999) - (sessionOrder[b] ?? 9999)) || a.localeCompare(b)
    );
    const laneEnd = []; // laneEnd[k] = dernière colonne occupée dans la ligne k
    const sidToLane = {};
    sidsByStart.forEach(sid => {
      let k = 0;
      while (laneEnd[k] !== undefined && laneEnd[k] >= span[sid].from) k++;
      laneEnd[k] = span[sid].to;
      sidToLane[sid] = k;
    });

    // 3. Construire les tableaux de lignes par jour
    let maxRowsWeek = 0;
    for (let col = 0; col < COLS; col++) {
      const day = weekDays[col];
      if (!day) continue;
      const dayEvs = eventsByDay[day] || [];

      const slots = [];
      const doublons = []; // même session présente plusieurs fois le même jour : jamais masquée
      dayEvs.forEach(ev => {
        const k = sidToLane[ev.session_id];
        while (slots.length <= k) slots.push(null);
        if (slots[k] === null) slots[k] = ev;
        else doublons.push(ev);
      });
      doublons.forEach(ev => {
        const emptyIdx = slots.indexOf(null);
        if (emptyIdx !== -1) slots[emptyIdx] = ev;
        else slots.push(ev);
      });

      const affiches = slots.slice(0, MAX_VISIBLE);
      slottedByDay[day] = affiches;
      extraByDay[day] = dayEvs.length - affiches.filter(Boolean).length;
      maxRowsWeek = Math.max(maxRowsWeek, affiches.length);
    }

    // 4. Hauteur de ligne : nombre de lignes réellement occupées (y compris celles qui ont des trous au-dessus)
    weekRowHeights.push(ROW_HEIGHTS[Math.min(maxRowsWeek, MAX_VISIBLE)] + 'px');

    // 5. Numéro de semaine ISO
    const fd = new Date(annee, mois - 1, 1);
    const toMon = (fd.getDay() + 6) % 7;
    weekNumbers.push(getISOWeek(new Date(annee, mois - 1, 1 - toMon + row * 7)));
  }

  // Légende salles — toutes les salles configurées (pas seulement celles du mois)
  const sallesPresentes = useMemo(() => {
    const toutes = (sallesData?.data || []).filter(s => s.couleur);
    if (toutes.length > 0) return toutes.map(s => ({ salle_id: s.salle_id, nom_salle: s.nom_salle, couleur: s.couleur }));
    // Fallback : salles extraites des événements du mois
    const seen = new Map();
    allEvents.forEach(ev => {
      if (ev.salle_id && !seen.has(ev.salle_id))
        seen.set(ev.salle_id, { salle_id: ev.salle_id, nom_salle: ev.nom_salle || ev.salle_id, couleur: ev.salle_couleur || '#6b7280' });
    });
    return [...seen.values()];
  }, [sallesData, allEvents]);

  return (
    <div className="page">
      <PageHeader
        title="Planning des formations"
        subtitle={`${MOIS_NOMS[mois - 1]} ${annee}`}
        actions={
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
            <BackButton />
            <button className="btn-secondary" onClick={() => setShowPrint(true)} title="Imprimer le planning">
              🖨 Imprimer
            </button>
            {hasRole('utilisateur') && (
              <button className="btn-primary" onClick={() => setModal(true)}>+ Nouvelle session</button>
            )}
          </div>
        }
      />

      {/* ── Ligne 1 : navigation ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn-secondary" onClick={prevMonth} style={{ padding: '0.4rem 0.75rem' }}>‹</button>
        <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', minWidth: '180px', textAlign: 'center' }}>
          {MOIS_NOMS[mois - 1]} {annee}
        </span>
        <button className="btn-secondary" onClick={nextMonth} style={{ padding: '0.4rem 0.75rem' }}>›</button>
        {(mois !== now.getMonth() + 1 || annee !== now.getFullYear()) && (
          <button className="btn-secondary" onClick={() => { setMois(now.getMonth() + 1); setAnnee(now.getFullYear()); }}
            style={{ padding: '0.35rem 0.8rem', fontSize: '0.83rem', fontWeight: 600 }}>
            Aujourd'hui
          </button>
        )}
        <label style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer',
          fontSize: '0.85rem', fontWeight: cinqJours ? 700 : 500, marginLeft: '0.5rem',
          padding: '0.35rem 0.75rem', borderRadius: '8px',
          border: `1.5px solid ${cinqJours ? 'var(--accent-blue)' : 'var(--border)'}`,
          background: cinqJours ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)',
          color: cinqJours ? 'var(--accent-blue)' : 'var(--text-secondary)',
          transition: 'all 0.15s',
        }}>
          <input type="checkbox" checked={cinqJours} onChange={e => setCinqJours(e.target.checked)} style={{ accentColor: 'var(--accent-blue)' }} />
          Semaine 5 jours
        </label>
      </div>

      {/* ── Ligne 2 : filtres ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <FilterDropdown label="Salle" options={optsSalle} value={filtSalle} onChange={setFiltSalle}
          colorMap={Object.fromEntries(sallesPresentes.map(s => [s.salle_id, s.couleur]))} />
        <FilterDropdown label="Lieu" options={optsLieu} value={filtLieu} onChange={setFiltLieu} />
        <FilterDropdown label="Formateur" options={optsFormateur} value={filtFormateur} onChange={setFiltFormateur} />
        <FilterDropdown label="Formation" options={optsFormation} value={filtFormation} onChange={setFiltFormation} />
        <FilterDropdown label="Créneau" options={optsSlot} value={filtSlot} onChange={setFiltSlot} />
        {nbFiltresActifs > 0 && (
          <>
            <button onClick={resetFiltres} style={{
              padding: '0.38rem 0.7rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.4)',
              background: 'rgba(239,68,68,0.08)', color: '#f87171', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
            }}>
              ✕ Réinitialiser ({nbFiltresActifs})
            </button>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
              {filteredEvents.length} / {allEvents.length} créneaux
            </span>
          </>
        )}
      </div>

      {/* ── Calendrier ── */}
      {loading ? <Loading /> : (
        <>
          <div className="calendar-grid" style={{
            gridTemplateColumns: `28px repeat(${COLS}, 1fr)`,
            gridTemplateRows: `auto ${weekRowHeights.join(' ')}`,
          }}>
            {/* Cellule vide au-dessus de la colonne numéros de semaine */}
            <div className="calendar-header-cell" style={{ background: 'none', border: 'none' }} />
            {JOURS_DISPLAY.map(j => <div key={j} className="calendar-header-cell">{j}</div>)}
            {cells.map((day, i) => (
              <React.Fragment key={i}>
                {i % COLS === 0 && (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--text-secondary)', lineHeight: 1.1, userSelect: 'none',
                    borderRight: '2px solid var(--border)', background: 'var(--bg-sidebar)',
                    gap: '1px',
                  }}>
                    <span style={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.04em', color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Sem</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent-blue)' }}>{weekNumbers[Math.floor(i / COLS)]}</span>
                  </div>
                )}
              <div className={`calendar-cell ${day ? '' : 'empty'} ${day === now.getDate() && mois === now.getMonth() + 1 && annee === now.getFullYear() ? 'today' : ''}`}>
                {day && (
                  <>
                    <span className="calendar-day-num">{day}</span>
                    <div className="calendar-events">
                      {(slottedByDay[day] || []).map((ev, j) => ev ? (() => {
                        const evInscritsLabel = (ev.nb_inscrits > 0 || ev.stagiaires_max > 0)
                          ? `${ev.nb_inscrits ?? 0}${ev.stagiaires_max ? `/${ev.stagiaires_max}` : ''}` : null;
                        const evInfoLabel = evInscritsLabel || '';
                        const evPlein = ev.stagiaires_max && ev.nb_inscrits >= ev.stagiaires_max;
                        const avColor = ev.avancement_color ? AVANCEMENT_HEX[ev.avancement_color] : '#ffffff';
                        return (
                          <div key={j} className="calendar-event"
                            style={{ borderLeftColor: getColor(ev), height: SLOT_H + 'px', overflow: 'hidden', flexShrink: 0, boxSizing: 'border-box' }}
                            onClick={() => navigate(`/sessions/${ev.session_id}`)}
                            onMouseEnter={e => handleEventEnter(e, ev)}
                            onMouseLeave={handleEventLeave}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.2rem' }}>
                              <span style={{ fontSize: '0.65rem', fontWeight: 800, color: avColor, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.session_id}</span>
                              {evInfoLabel && (
                                <span className="cal-event-time" style={{ whiteSpace: 'nowrap', color: evPlein ? '#f87171' : 'rgba(255,255,255,0.82)', flexShrink: 0 }}>
                                  {evInfoLabel}
                                </span>
                              )}
                            </div>
                            {(ev.nom_dossier || ev.intitule_formation) && (
                              <span className="cal-event-name" style={{ color: avColor, fontWeight: 700 }}>
                                {(ev.nom_dossier || ev.intitule_formation || '').substring(0, 22)}
                              </span>
                            )}
                            {ev.modules_du_jour && (
                              <span style={{ fontSize: '0.61rem', color: 'rgba(255,255,255,0.80)', display: 'block', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                                ↳ {ev.modules_du_jour}
                              </span>
                            )}
                            {ev.nom_salle && <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.62)', display: 'block', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.nom_salle}</span>}
                          </div>
                        );
                      })() : (
                        <div key={j} style={{ height: SLOT_H + 'px', flexShrink: 0 }} />
                      ))}
                      {(extraByDay[day] || 0) > 0 && (
                        <span className="calendar-more">+{extraByDay[day]} autres</span>
                      )}
                    </div>
                  </>
                )}
              </div>
              </React.Fragment>
            ))}
          </div>

          {/* Légende */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem', padding: '0.5rem 1rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Légende :</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'transparent', border: '1.5px solid rgba(255,255,255,0.45)', flexShrink: 0, display: 'inline-block' }} />
              Session sans stagiaire
            </span>
          </div>

          {/* Légende avancement documentaire */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem', padding: '0.5rem 1rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avancement :</span>
            {[
              { color: '#dc2626', label: 'Au moins 1 doc. manquant' },
              { color: '#16a34a', label: 'Tous docs complétés' },
            ].map(({ color, label }) => (
              <span key={color} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 700, color }}>{label}</span>
              </span>
            ))}
          </div>

          {/* Légende salles */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', padding: '0.5rem 1rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Salles :</span>
            {sallesPresentes.map(s => (
              <span key={s.salle_id}
                onClick={() => setFiltSalle(filtSalle === s.salle_id ? null : s.salle_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', cursor: 'pointer',
                  color: filtSalle === s.salle_id ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontWeight: filtSalle === s.salle_id ? 700 : 400,
                  padding: '0.15rem 0.5rem', borderRadius: '12px',
                  background: filtSalle === s.salle_id ? 'var(--bg-elevated)' : 'transparent',
                  border: `1px solid ${filtSalle === s.salle_id ? s.couleur : 'transparent'}`,
                  transition: 'all 0.15s',
                }}>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '3px', background: s.couleur, flexShrink: 0 }} />
                {s.nom_salle}
              </span>
            ))}
            {sallesPresentes.length === 0 && (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Aucune salle avec couleur configurée</span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', paddingLeft: '0.75rem', borderLeft: '1px solid var(--border)' }}>
              <span style={{ display: 'inline-block', width: '3px', height: '16px', borderRadius: '2px', background: '#64748b', flexShrink: 0 }} />
              Sans salle
            </span>
          </div>
        </>
      )}

      {/* Tooltip session au survol */}
      {tooltip && (
        <SessionTooltip
          ev={tooltip.ev}
          pos={tooltip.pos}
          sessionDetail={tooltipDetail}
          loading={tooltipLoading}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
        />
      )}

      {/* Modale création session */}
      {modal && (
        <ModalNouvelleSession
          onSave={() => { setModal(false); setRefresh(r => r + 1); }}
          onClose={() => setModal(false)}
        />
      )}

      {/* Modale impression */}
      {showPrint && (
        <PrintModal annee={annee} mois={mois} onClose={() => setShowPrint(false)} />
      )}
    </div>
  );
}
